import { diaCivilNaFazenda, somarDiasCivis } from "./data-fazenda.js";
import { dataCurtaBR } from "./boletim-periodo.js";
import { ORIGEM_CONFERENCIA, type ConferenciaDTO } from "./mercado-conferencia.js";
import type { PendenciaOfertaDTO, ResumoOfertaHome } from "./mercado-ofertas.js";

/**
 * Contrato do link público do Mercado (C1 + a conferência do C3).
 *
 * ⚠️ Este arquivo é a fonte da verdade do que o corretor pode ver. `CHAVES_DTO_PUBLICO`
 * existe para o teste de contrato: acrescentar campo ao DTO sem passar por aqui quebra
 * o teste, que é exatamente o objetivo — do outro lado do link tem alguém que não é da
 * fazenda, e "vazou junto" nunca pode ser um acidente de spread.
 *
 * NADA de custo, diária, manejo, outro comprador, ids internos ou orgId.
 */

export interface ItemPublicoDTO {
  variedade: string;
  qualidade: string | null;
  kg: number;
  precoKg: number;
  subtotal: number;
}

export interface RomaneioPublicoDTO {
  /** "012/2026". */
  numero: string;
  /** ISO "AAAA-MM-DD". */
  dataEmissao: string;
  /** ISO "AAAA-MM-DD" ou null. */
  vencimento: string | null;
  produtor: { nome: string; cidade: string | null };
  itens: ItemPublicoDTO[];
  totalKg: number;
  totalValor: number;
  statusCarga: string;
  statusPagamento: string;
  /** C3 — o que o comprador conferiu, ou `null` se ainda não conferiu. */
  conferencia: ConferenciaDTO | null;
  /** C3 — o botão "Os kg batem?" aparece? Falso em venda cancelada ou link morto. */
  podeConferir: boolean;
}

export const CHAVES_DTO_PUBLICO = [
  "conferencia",
  "podeConferir",
  "numero",
  "dataEmissao",
  "vencimento",
  "produtor",
  "itens",
  "totalKg",
  "totalValor",
  "statusCarga",
  "statusPagamento",
] as const;

export const CHAVES_PRODUTOR_PUBLICO = ["nome", "cidade"] as const;

export const CHAVES_ITEM_PUBLICO = ["variedade", "qualidade", "kg", "precoKg", "subtotal"] as const;

/** O lado do corretor de um romaneio, como a tela do PRODUTOR o enxerga. Sem token:
 *  quem precisa do link é o endpoint de mensagens, não a lista. */
export interface CompartilhamentoResumo {
  id: string;
  vendaId: string;
  compradorId: string;
  destinoTelefone: string;
  statusCarga: string;
  statusPagamento: string;
  enviadoEm: string | null;
  vistoEm: string | null;
  revogadoEm: string | null;
  criadoEm: string;
  /**
   * O pagamento que o COMPRADOR registrou neste romaneio — o mais recente, e
   * `null` quando ele não registrou nenhum.
   *
   * É o que a venda do produtor mostra como "Sávio confirmou o pagamento no
   * Rizoma". Contestado não entra: não é pagamento, é disputa.
   */
  pagamentoDoComprador: {
    /** O lançamento — é ele que o "Não recebi" contesta, e não "o último" no clique. */
    id: string;
    valor: number;
    /** O dia do DINHEIRO, informado por quem pagou (AAAA-MM-DD). */
    data: string;
    /** O instante do registro no app (ISO). */
    registradoEm: string;
  } | null;
  /**
   * O comprador tem conta no Rizoma E vínculo ATIVO com este produtor — o mesmo
   * critério de `resolverOrgCompradora` (quem decide se o romaneio é entregue).
   * Calculado no SERVIDOR: é o que tira "Enviado/Visto" do chip, porque com
   * conta o romaneio formalizado já está na tela dele.
   */
  compradorNoRizoma: boolean;
  /**
   * Há conferência VÁLIDA (não invalidada) do comprador — bata ela ou não. É o
   * que diz à venda se existe a seção "Conferência de {comprador}".
   */
  conferido: boolean;
  /** Quando essa conferência válida foi registrada (ou corrigida por último), ISO. */
  conferidoEm: string | null;
  /** A conferência válida que NÃO bate com o emitido; `null` quando bate ou não há. */
  divergencia: DivergenciaDTO | null;
  /**
   * O "Não recebi" do produtor que ainda vale: o lançamento do comprador que ele
   * contestou, enquanto o status do dinheiro for CONTESTADO. `null` fora disso.
   */
  contestacao: {
    motivo: string | null;
    /** Quando o produtor contestou (ISO). */
    em: string;
    valor: number;
  } | null;
  /** `valor_conferido` da conferência VÁLIDA; `null` sem conferência. */
  valorConferido: number | null;
  /**
   * O que falta receber: base (conferido válido manda sobre o emitido) − pago.
   * É o valor de "Marcar como paga — R$ …" e do "Recebi" da venda — o mesmo que
   * o servidor lança ao quitar (`lerAntesDeMarcarPaga`). 0 quando quitada.
   */
  aReceber: number;
}

/**
 * UMA mensagem, não duas.
 *
 * Eram `apresentacao` + `nota`, enviadas em sequência. Duas mensagens do mesmo
 * número com segundos de diferença têm cara de disparo automático; uma só, que abre
 * com "aqui é o Hugo" e emenda o romaneio, é conversa — e corta o envio manual pela
 * metade. `primeiraVez` diz qual variante foi montada (A com saudação e preâmbulo,
 * B direto no romaneio); a tela usa isso para contexto, nunca para remontar texto.
 */
export interface MensagemCompartilhamento {
  texto: string;
  urlWhatsApp: string;
  primeiraVez: boolean;
}

// ─── A FORMALIZAÇÃO termina em "Formalizada" ─────────────────────────────────
//
// ⚠️ UMA derivação para o Cacho (Vendas) e para o Mercado (A receber,
// Compradores · detalhe). Eram duas — `estadoFormalizacao` com ENVIADO/VISTO e
// `derivarEstadoFormalizacao` com ENVIADA/VISTA — e as duas mediam a coisa
// errada depois do #241: com o comprador no Rizoma, formalizar JÁ entrega o
// romaneio na tela dele, e "Enviado"/"Visto" medem só o link do WhatsApp. A
// Fazenda Boa Vista via o 046 "Visto" e o 048–051 "Formalizada", e os cinco
// estavam na mesma situação para o Sávio.
//
// O link continua existindo para quem NÃO tem conta — é o único canal — e por
// isso vira uma SUBLINHA do chip, não um degrau dele. `enviado_em` e `visto_em`
// continuam gravados; só saem do chip.

export type EstadoFormalizacao = "NAO_FORMALIZADA" | "FORMALIZADA" | "REVOGADO";

/** Onde está o link, quando o link é o canal (comprador sem conta). */
export type EstadoDoLink = "NAO_ENVIADO" | "ENVIADO" | "ABERTO";

export interface FormalizacaoDoRomaneio {
  estado: EstadoFormalizacao;
  /** `null` com comprador no Rizoma (o link não é o canal) e fora de FORMALIZADA. */
  link: { estado: EstadoDoLink; em: string | null } | null;
}

export function derivarFormalizacao(entrada: {
  /** Existe compartilhamento (revogado ou não). */
  formalizada: boolean;
  revogado?: boolean;
  compradorNoRizoma: boolean;
  enviadoEm: string | null;
  vistoEm: string | null;
}): FormalizacaoDoRomaneio {
  if (!entrada.formalizada) return { estado: "NAO_FORMALIZADA", link: null };
  if (entrada.revogado) return { estado: "REVOGADO", link: null };
  if (entrada.compradorNoRizoma) return { estado: "FORMALIZADA", link: null };
  if (entrada.vistoEm) return { estado: "FORMALIZADA", link: { estado: "ABERTO", em: entrada.vistoEm } };
  if (entrada.enviadoEm) return { estado: "FORMALIZADA", link: { estado: "ENVIADO", em: entrada.enviadoEm } };
  return { estado: "FORMALIZADA", link: { estado: "NAO_ENVIADO", em: null } };
}

/** O chip da lista de Vendas, a partir do compartilhamento que VALE (ou nenhum). */
export function formalizacaoDoCompartilhamento(c: CompartilhamentoResumo | undefined): FormalizacaoDoRomaneio {
  if (!c) return derivarFormalizacao({ formalizada: false, compradorNoRizoma: false, enviadoEm: null, vistoEm: null });
  return derivarFormalizacao({
    formalizada: true,
    revogado: !!c.revogadoEm,
    compradorNoRizoma: c.compradorNoRizoma,
    enviadoEm: c.enviadoEm,
    vistoEm: c.vistoEm,
  });
}

/**
 * A sublinha do chip: "link não enviado" · "link enviado 10/09" · "link aberto 10/09".
 *
 * Âmbar SÓ em "não enviado" de nota em aberto — é a única que pede ação (o
 * comprador sem conta não sabe que a nota existe). Em nota paga é histórico: o
 * backfill do C2 formalizou dezenas de notas quitadas sem mandar link nenhum, e
 * pintá-las de âmbar encheria a lista de pendência que não existe.
 */
export function textoDoLink(
  link: FormalizacaoDoRomaneio["link"],
  opcoes: { emAberto: boolean } = { emAberto: true },
): { texto: string; atencao: boolean } | null {
  if (!link) return null;
  if (link.estado === "NAO_ENVIADO" || !link.em) return { texto: "link não enviado", atencao: opcoes.emAberto };
  const dia = dataCurtaBR(diaCivilNaFazenda(link.em));
  return { texto: link.estado === "ABERTO" ? `link aberto ${dia}` : `link enviado ${dia}`, atencao: false };
}

/** O tooltip do chip "Formalizada" — diz POR ONDE o romaneio chegou. */
export function dicaDaFormalizacao(f: FormalizacaoDoRomaneio, compradorNome: string): string | null {
  if (f.estado !== "FORMALIZADA") return null;
  return f.link ? `Enviada por link a ${compradorNome}.` : `Na tela de ${compradorNome} no Rizoma.`;
}

/**
 * O comprador está no Rizoma? Conta (`compradores.org_vinculada_id`) E vínculo
 * ATIVO com esta org — o critério de `resolverOrgCompradora`, que é quem decide
 * se o romaneio formalizado é entregue. Vínculo suspenso ou revogado = o link
 * volta a ser o canal.
 */
export function compradorEstaNoRizoma(
  orgVinculadaId: string | null | undefined,
  orgsComVinculoAtivo: readonly string[],
): boolean {
  return !!orgVinculadaId && orgsComVinculoAtivo.indexOf(orgVinculadaId) >= 0;
}

// ─── O DINHEIRO da venda, no chip de Status ──────────────────────────────────
//
// ⚠️ QUEM REGISTRA QUITA (#246, reafirmado pelo Hugo em 24/09): o pagamento que
// o comprador registra já chega PAGO, sem segundo ato. Não existe "Informado ·
// confirme". O que existe é o caminho de volta — o "Não recebi" do produtor —,
// que deixa o romaneio CONTESTADO até o comprador registrar de novo.
//
// UM derivador para Vendas (Cacho) e A receber (Mercado): os dois pintam a
// mesma venda, e cada um com a sua regra foi como o Cacho e o Mercado já
// chegaram a discordar sobre a mesma nota.

export type EstadoDinheiroVenda = "RASCUNHO" | "A_RECEBER" | "PARCIAL" | "CONTESTADO" | "PAGA" | "CANCELADA";

/**
 * Em caixa normal, como "Formalizada" e "Divergência · −120 kg", que dividem a
 * célula de Status com ele — "A RECEBER" em cima de "Formalizada" eram dois
 * chips de sistemas diferentes (print do Hugo, 24/09).
 */
export const ROTULO_DINHEIRO_VENDA: Record<EstadoDinheiroVenda, string> = {
  RASCUNHO: "Rascunho",
  A_RECEBER: "A receber",
  PARCIAL: "Parcial",
  CONTESTADO: "Contestado",
  PAGA: "Paga",
  CANCELADA: "Cancelada",
};

/**
 * `statusPagamento` é o do compartilhamento que VALE (materializado pelo escritor
 * único), ou `null` quando a venda não foi formalizada — aí só há A RECEBER e
 * PAGA, porque ninguém do outro lado registra nada.
 *
 * ⚠️ O STATUS DA VENDA MANDA sobre PAGA/não PAGA: é ele que soma no "A receber"
 * do Cacho, e um chip dizendo PAGA numa linha que o card do topo conta como a
 * receber seria a tela contradizendo a si mesma. Os lançamentos só DETALHAM a
 * venda em aberto: parcial ou contestada.
 */
export function estadoDinheiroDaVenda(entrada: {
  statusVenda: string;
  statusPagamento: string | null;
}): EstadoDinheiroVenda {
  if (entrada.statusVenda === "RASCUNHO") return "RASCUNHO";
  if (entrada.statusVenda === "CANCELADA") return "CANCELADA";
  if (entrada.statusVenda === "PAGA") return "PAGA";
  if (entrada.statusPagamento === "CONTESTADO") return "CONTESTADO";
  if (entrada.statusPagamento === "PARCIAL" || entrada.statusPagamento === "PARCIAL_INFORMADO") return "PARCIAL";
  return "A_RECEBER";
}

/** O dinheiro ainda não entrou todo — é o que decide o botão "Recebi". */
export function dinheiroEmAberto(estado: EstadoDinheiroVenda): boolean {
  return estado === "A_RECEBER" || estado === "PARCIAL" || estado === "CONTESTADO";
}

// ─── A LINHA DO TEMPO da venda ───────────────────────────────────────────────
//
// O topo do detalhe da venda: Criada → Emitida → Formalizada → Conferida →
// Paga, com a data em cada etapa. Substitui três blocos que se repetiam
// embaixo — o card "Na tela de Sávio" com o "Enviado ▸ 15:10", a faixa verde
// "Sávio registrou o pagamento" e a tabela da conferência que BATE.
//
// ⚠️ DETALHE SÓ QUANDO PEDE ATENÇÃO (Hugo, 24/09: "na tela de Sávio" e "kg
// batem" embaixo de uma etapa já marcada era detalhe demais). Etapa feita e
// normal = bolinha verde + data, e a frase vai para o tooltip. A linha de
// detalhe fica para o que o produtor precisa ver sem passar o mouse: "−120 kg",
// "revogada", "link não enviado", e o prazo do dinheiro que falta ("vence
// 09/10", "venceu", "contestada", "parcial", "sem vencimento").
//
// ⚠️ As etapas NÃO são uma escada: o comprador pode pagar sem conferir, e a
// conferência pode vir depois do pagamento. Cada etapa diz se aconteceu; o
// conector só fica verde entre duas que aconteceram.

export type ChaveEtapa = "CRIADA" | "EMITIDA" | "FORMALIZADA" | "CONFERIDA" | "PAGA";

export interface EtapaDaVenda {
  chave: ChaveEtapa;
  rotulo: string;
  feita: boolean;
  /** "13/09" — o dia civil da fazenda em que aconteceu. */
  quando: string | null;
  /** Só o que pede atenção, ou o prazo do que falta: "−120 kg", "link não enviado", "vence 24/09". */
  detalhe: string | null;
  tom: "neutro" | "sucesso" | "atencao" | "erro";
  /** A frase inteira, para o tooltip — ex.: "Sávio registrou o pagamento no Rizoma em 22/09." */
  dica: string | null;
}

function diaCurto(instante: string | null | undefined): string | null {
  if (!instante) return null;
  return dataCurtaBR(instante.length === 10 ? instante : diaCivilNaFazenda(instante));
}

function diasAte(de: string, ate: string): number {
  const a = Date.UTC(Number(de.slice(0, 4)), Number(de.slice(5, 7)) - 1, Number(de.slice(8, 10)));
  const b = Date.UTC(Number(ate.slice(0, 4)), Number(ate.slice(5, 7)) - 1, Number(ate.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}

export function linhaDoTempoDaVenda(e: {
  status: string;
  /** ISO — `vendas.created_at`. */
  criadaEm: string;
  emitidaEm: string | null;
  pagaEm: string | null;
  /** AAAA-MM-DD. */
  vencimento: string | null;
  /**
   * Quem escreveu o vencimento por último (`vendas.vencimento_definido_por`).
   * "COMPRADOR" = ele mudou o prazo (no PATCH ou na conferência, 24/09) — o
   * produtor precisa ver isso sem abrir histórico nenhum.
   */
  vencimentoDefinidoPor?: string | null;
  /** O compartilhamento que VALE (o vivo; o revogado só se não houver outro). */
  compartilhamento: CompartilhamentoResumo | undefined;
  compradorNome: string;
  /** Hoje na fazenda, AAAA-MM-DD. */
  hoje: string;
}): EtapaDaVenda[] {
  const c = e.compartilhamento;
  const vivo = c && !c.revogadoEm ? c : undefined;
  const emitida = e.status === "EMITIDA" || e.status === "PAGA";
  const paga = e.status === "PAGA";
  const dinheiro = estadoDinheiroDaVenda({ statusVenda: e.status, statusPagamento: vivo ? vivo.statusPagamento : null });

  // Formalizada — na linha só "revogada" e "link não enviado" (o que o produtor
  // ainda tem de fazer). ONDE está ("na tela de Sávio", "link aberto 10/09") vai
  // para o tooltip.
  const formalizacao = formalizacaoDoCompartilhamento(c);
  const link = textoDoLink(formalizacao.link, { emAberto: !paga });
  const dicaFormalizacao = dicaDaFormalizacao(formalizacao, e.compradorNome);
  const formalizada: EtapaDaVenda = {
    chave: "FORMALIZADA", rotulo: "Formalizada",
    feita: formalizacao.estado === "FORMALIZADA",
    quando: formalizacao.estado === "FORMALIZADA" ? diaCurto(c?.criadoEm) : null,
    detalhe: formalizacao.estado === "REVOGADO" ? "revogada"
      : formalizacao.estado === "FORMALIZADA" && link?.atencao ? link.texto
        : null,
    tom: formalizacao.estado === "REVOGADO" ? "erro" : link?.atencao ? "atencao" : "neutro",
    dica: dicaFormalizacao && link && !link.atencao
      ? `${dicaFormalizacao} ${link.texto.charAt(0).toUpperCase()}${link.texto.slice(1)}.`
      : dicaFormalizacao,
  };

  // Conferida — kg que batem é a bolinha verde e só; na linha, apenas o delta
  // da divergência (que leva à tabela lado a lado, embaixo).
  const conferida: EtapaDaVenda = {
    chave: "CONFERIDA", rotulo: "Conferida",
    feita: !!vivo?.conferido,
    quando: vivo?.conferido ? diaCurto(vivo.conferidoEm) : null,
    detalhe: vivo?.conferido && vivo.divergencia
      ? rotuloDivergencia(vivo.divergencia).replace(/^Divergência · /, "") || "divergência"
      : null,
    tom: vivo?.divergencia ? "erro" : "neutro",
    dica: !vivo?.conferido ? null
      : `${e.compradorNome} conferiu os kg em ${diaCurto(vivo.conferidoEm) ?? "—"}${vivo.divergencia ? "." : " — batem com o romaneio."}`,
  };

  // Paga — feita, a data e só (quem pagou vai para o tooltip); em aberto, o
  // prazo (ou o que trava o dinheiro).
  let detalhePaga: string | null = null;
  let tomPaga: EtapaDaVenda["tom"] = "neutro";
  let dicaPaga: string | null = null;
  if (paga) {
    dicaPaga = vivo?.pagamentoDoComprador
      ? `${e.compradorNome} registrou o pagamento no Rizoma em ${diaCurto(vivo.pagamentoDoComprador.registradoEm)}.`
      : e.pagaEm ? `Marcada como paga em ${diaCurto(e.pagaEm)}.` : null;
  } else if (emitida) {
    if (dinheiro === "CONTESTADO") { detalhePaga = "contestada"; tomPaga = "erro"; }
    else if (dinheiro === "PARCIAL") { detalhePaga = "parcial"; tomPaga = "atencao"; }
    else if (!e.vencimento) detalhePaga = "sem vencimento";
    else {
      const dias = diasAte(e.hoje, e.vencimento);
      detalhePaga = dias < 0 ? `venceu ${dataCurtaBR(e.vencimento)}` : dias === 0 ? "vence hoje" : `vence ${dataCurtaBR(e.vencimento)}`;
      tomPaga = dias < 0 ? "erro" : dias <= 3 ? "atencao" : "neutro";
      // O comprador definiu o prazo: é o outro lado num termo seu — vai na
      // linha, não só no tooltip. O TOM continua sendo o do prazo: a adoção de
      // um registro dele que já trazia vencimento também grava COMPRADOR, e
      // pintar isso de âmbar seria alarme sem nada a fazer.
      if (e.vencimentoDefinidoPor === "COMPRADOR") {
        detalhePaga = `${detalhePaga} · por ${e.compradorNome}`;
        dicaPaga = `Vencimento definido por ${e.compradorNome}.`;
      }
    }
  }

  return [
    { chave: "CRIADA", rotulo: "Criada", feita: true, quando: diaCurto(e.criadaEm), detalhe: null, tom: "neutro", dica: null },
    { chave: "EMITIDA", rotulo: "Emitida", feita: emitida, quando: emitida ? diaCurto(e.emitidaEm) : null, detalhe: null, tom: "neutro", dica: null },
    formalizada,
    conferida,
    { chave: "PAGA", rotulo: "Paga", feita: paga, quando: paga ? diaCurto(e.pagaEm) : null, detalhe: detalhePaga, tom: tomPaga, dica: dicaPaga },
  ];
}

// ─── A DIVERGÊNCIA da conferência ────────────────────────────────────────────
//
// Sai de `montarConferenciaDTO` — o MESMO DTO que o comprador e o link público
// leem. Nenhuma conta nova de delta: a conferência já traz `bate`, `deltaKg`,
// `valorRomaneio` e `valorConferido`, e uma segunda conta aqui divergiria dela
// no primeiro arredondamento.

export interface DivergenciaDTO {
  /** conferido − emitido (negativo = chegou menos). */
  deltaKg: number;
  /** R$ conferido − R$ emitido. */
  deltaValor: number;
  /** O comprador registrou outro R$/kg em alguma linha (24/09). */
  precoDivergente: boolean;
}

/**
 * `null` quando não há conferência, quando ela BATE, ou quando foi invalidada
 * (o produtor corrigiu a venda depois — o documento voltou a valer). Conferido
 * e igual não é divergência: fica no histórico, não vira chip.
 */
export function divergenciaDaConferencia(c: ConferenciaDTO | null | undefined): DivergenciaDTO | null {
  if (!c || c.invalidadaEm || c.bate) return null;
  return {
    deltaKg: c.deltaKg,
    deltaValor: Math.round((c.valorConferido - c.valorRomaneio) * 100) / 100,
    precoDivergente: !!c.precoDivergente,
  };
}

// ─── O VENCIMENTO no histórico (25/09) ───────────────────────────────────────
//
// "A linha do tempo não mostra quem mudou nem quando" (Hugo, 046). A MESMA frase
// nos dois históricos — o da venda (produtor) e o painel do romaneio
// (comprador) —, com o ator PELO NOME e a origem quando a troca veio na
// conferência: "Sávio mudou o vencimento de 24/09 para 09/10 na conferência".

/**
 * `quem` é o nome do ator (primeiro nome), ou um genérico ("O produtor") quando
 * o nome não existe; `null` = frase impessoal. `naConferencia` = a troca foi
 * feita DENTRO da conferência (origem CONFERENCIA, ou o evento gravado na mesma
 * transação da conferência — ver `vencimentoNaConferencia`).
 */
export function textoDoVencimentoAlterado(
  payload: Record<string, unknown> | null | undefined,
  quem: string | null,
  naConferencia: boolean,
): string {
  const data = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? dataCurtaBR(v) : null);
  const de = data(payload?.de);
  const para = data(payload?.para);
  const onde = naConferencia ? " na conferência" : "";
  if (payload && "para" in payload && payload.para === null) {
    return quem ? `${quem} tirou o vencimento${onde}` : `Vencimento retirado${onde}`;
  }
  if (!para) return quem ? `${quem} mudou o vencimento${onde}` : `Vencimento alterado${onde}`;
  if (!de) return quem ? `${quem} definiu o vencimento para ${para}${onde}` : `Vencimento definido para ${para}${onde}`;
  return quem
    ? `${quem} mudou o vencimento de ${de} para ${para}${onde}`
    : `Vencimento alterado de ${de} para ${para}${onde}`;
}

/**
 * Esta troca de vencimento veio DENTRO de uma conferência?
 *
 * Desde 25/09 o evento sai com origem CONFERENCIA. Os de antes (o 046, de
 * 24/09) saíram com origem APP — mas a conferência grava o VENCIMENTO_ALTERADO
 * na MESMA transação da CONFERENCIA_REGISTRADA, e `criado_em` é o `now()` da
 * transação: os dois têm o mesmo instante. É esse fato que se lê aqui, sem
 * reescrever evento nenhum.
 */
export function vencimentoNaConferencia(
  evento: { origem: string; criadoEm: Date | string },
  outros: Array<{ tipo: string; criadoEm: Date | string }>,
): boolean {
  if (evento.origem === ORIGEM_CONFERENCIA) return true;
  const t = new Date(evento.criadoEm).getTime();
  return outros.some(o => o.tipo === "CONFERENCIA_REGISTRADA" && new Date(o.criadoEm).getTime() === t);
}

/** Uma linha do histórico da venda (produtor) — frase pronta, como a do comprador. */
export interface EventoDaVendaDTO {
  tipo: string;
  texto: string;
  /** ISO. */
  em: string;
}

/** "12345" → "12.345" — sem `toLocaleString`: o ICU reduzido da Vercel já entregou en-US. */
function milharBR(inteiro: number): string {
  return String(inteiro).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * "Divergência · −120 kg". Sem diferença no TOTAL de kg (trocou 1ª por 2ª),
 * o valor é o que mudou: "Divergência · −R$ 45,00". O sinal é o "−" tipográfico.
 *
 * Com o PREÇO divergente, o rótulo é sempre em R$: "−120 kg" calaria que o
 * R$/kg também caiu, e o que o produtor precisa saber de relance é quanto
 * dinheiro a menos o comprador reconhece.
 */
export function rotuloDivergencia(d: DivergenciaDTO): string {
  const sinal = (n: number) => (n < 0 ? "−" : "+");
  const centavos = Math.round(Math.abs(d.deltaValor) * 100);
  if (Math.round(d.deltaKg) !== 0 && !(d.precoDivergente && centavos > 0)) {
    return `Divergência · ${sinal(d.deltaKg)}${milharBR(Math.abs(Math.round(d.deltaKg)))} kg`;
  }
  if (centavos > 0) {
    const reais = milharBR(Math.floor(centavos / 100));
    const resto = String(centavos % 100).padStart(2, "0");
    return `Divergência · ${sinal(d.deltaValor)}R$ ${reais},${resto}`;
  }
  return d.precoDivergente ? "Divergência · preço" : "Divergência";
}

// ─── Telefone do WhatsApp ────────────────────────────────────────────────────
//
// Moram aqui, e não em schema.ts, porque o modal de formalização precisa validar o
// campo no client — e schema.ts arrasta o drizzle inteiro junto.

/** Só dígitos. "(74) 99999-8888" → "74999998888". */
export function normalizarTelefoneBR(valor: string): string {
  return valor.replace(/\D/g, "");
}

/**
 * Celular brasileiro com DDD: 11 dígitos (2 de DDD + 9 do número, o primeiro deles
 * sempre 9). Aceita o número já com o 55 na frente e descarta o prefixo — colar
 * "5574999998888" direto do WhatsApp é o erro mais provável, e recusar isso seria
 * recusar justamente o número certo.
 */
export function telefoneWhatsappValido(valor: string): boolean {
  const digitos = normalizarTelefoneBR(valor);
  const semPais = digitos.length === 13 && digitos.substring(0, 2) === "55" ? digitos.substring(2) : digitos;
  return /^[1-9][0-9]9[0-9]{8}$/.test(semPais);
}

/**
 * A CHAVE do telefone: dígitos, sem o código do país.
 *
 * `normalizarTelefoneBR` devolve os dígitos crus — bom para exibir e para montar o
 * link wa.me (que quer o 55 na frente). Mas como IDENTIDADE, "5574999998888" e
 * "74999998888" são a mesma pessoa, e é isso que o login do corretor e o claim
 * comparam. Sem esta função, quem digitasse o número com o 55 no login receberia
 * "credenciais inválidas" com a senha certa — foi o bug que o teste pegou.
 *
 * 12 ou 13 dígitos começando em 55 = com código do país (o fixo tem 10, o celular 11).
 */
export function chaveTelefone(valor: string): string {
  const d = normalizarTelefoneBR(valor);
  if (d.length > 11 && d.substring(0, 2) === "55") return d.substring(2);
  return d;
}

/**
 * `https://wa.me/<E.164 sem o +>?text=<url-encoded>` — o link que abre a
 * conversa no WhatsApp DO PRÓPRIO produtor, com o texto já digitado.
 *
 * ⚠️ Mora no `shared/` porque os dois lados montam link: o servidor para cada
 * comprador do cadastro, e a tela para o "outro número" digitado na hora. Duas
 * implementações divergiriam no `55` — e o sintoma seria uma conversa que abre
 * em branco, sem erro nenhum.
 *
 * ⚠️ A plataforma NÃO manda mensagem. Ela monta o texto e abre a conversa;
 * quem toca em enviar é o produtor. Número de plataforma mandando oferta para
 * desconhecido é o padrão que o WhatsApp trata como maior risco de bloqueio.
 */
export function linkWhatsAppCom(telefone: string, texto: string): string {
  const digitos = normalizarTelefoneBR(telefone);
  const comPais = digitos.substring(0, 2) === "55" ? digitos : `55${digitos}`;
  return `https://wa.me/${comPais}?text=${encodeURIComponent(texto)}`;
}

/** "74999998888" → "(74) 99999-8888". Máscara progressiva, para digitação. */
export function mascararTelefoneBR(valor: string): string {
  const bruto = normalizarTelefoneBR(valor);
  // Colou com o 55 na frente: o prefixo sai antes do corte, senão a máscara comeria
  // o final do número e o produtor veria "(55) 74999-9988".
  const semPais = bruto.length > 11 && bruto.substring(0, 2) === "55" ? bruto.substring(2) : bruto;
  const d = semPais.substring(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 7) return `(${d.substring(0, 2)}) ${d.substring(2)}`;
  return `(${d.substring(0, 2)}) ${d.substring(2, 7)}-${d.substring(7)}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// C2 — conta do corretor
// ═══════════════════════════════════════════════════════════════════════════

/**
 * O estado de pagamento de um compartilhamento.
 *
 * ⚠️ PONTO ÚNICO. Enquanto `mercado_pagamentos` não existe (C4), o estado é DERIVADO
 * da venda: venda paga → PAGO, senão A_PAGAR. Quando os lançamentos chegarem, é aqui
 * que eles entram — e não num segundo caminho ao lado, que é como duas verdades sobre
 * o mesmo número começam a divergir.
 *
 * Por que isso importa agora: o produtor vai formalizar o histórico em silêncio, e
 * boa parte dele já está quitada. Corretor que abre a conta e vê R$ 200 mil "a pagar"
 * de notas que ele já pagou lê a tela como cobrança — o oposto do objetivo.
 */
export function derivarStatusPagamento(entrada: {
  /** Status da venda no Cacho: RASCUNHO | EMITIDA | PAGA | CANCELADA. */
  statusVenda: string;
  /** C4: soma dos lançamentos daquele compartilhamento. Hoje sempre ausente. */
  totalPago?: number;
  valorTotal?: number;
  /**
   * C3: o valor que o COMPRADOR conferiu, quando existe e não foi invalidado por
   * uma correção posterior da venda.
   *
   * Quando existe, ele — e não o total do romaneio — é a BASE de "quanto falta
   * pagar": o que vai ser pago é o que chegou na balança do comprador. É a única
   * mudança que o C3 faz nesta função, e ela é aqui de propósito: base do
   * pagamento tem UM lugar, e é este.
   */
  valorConferido?: number | null;
}): "PAGO" | "A_PAGAR" {
  // A base: conferido manda sobre o total do romaneio.
  const base = entrada.valorConferido != null && entrada.valorConferido > 0
    ? entrada.valorConferido
    : entrada.valorTotal;

  // C4 entra por aqui: havendo lançamento que cubra a base, ele MANDA sobre o
  // status da venda — o pagamento ao comprador é o fato mais específico.
  if (entrada.totalPago != null && base != null && base > 0) {
    return entrada.totalPago >= base ? "PAGO" : "A_PAGAR";
  }
  return entrada.statusVenda === "PAGA" ? "PAGO" : "A_PAGAR";
}

// ─── As duas datas do dinheiro ───────────────────────────────────────────────
//
// Vencimento (quando o dinheiro DEVE entrar) e recebimento (quando ENTROU). As
// regras moram aqui, compartilhadas, porque as duas são digitadas em mais de uma
// tela e conferidas no servidor: uma cópia da regra no client é a cópia que
// diverge quando alguém muda só um lado.

/**
 * Os atalhos do seletor de vencimento, em dias.
 *
 * ⚠️ Contam da DATA DA VENDA, nunca de hoje: o romaneio de 27/07 com "15 dias"
 * vence em 11/08 — vencido —, e é essa a verdade. Contar de hoje transformaria
 * cada toque num prazo novo, inventado na hora da digitação.
 *
 * Não existe prazo padrão por comprador, e é decisão, não esquecimento: uma
 * coluna `prazo_dias` no comprador afirmaria que cada um paga sempre no mesmo
 * prazo, e os dados não dizem isso.
 */
export const ATALHOS_VENCIMENTO_DIAS = [7, 15, 30] as const;

export function vencimentoDoAtalho(dataVenda: string, dias: number): string {
  return somarDiasCivis(dataVenda, dias);
}

/** Por que a data foi recusada, ou `null` quando ela serve. */
export type FalhaDeData = "ANTES_DA_VENDA" | "NO_FUTURO";

/**
 * O vencimento pode estar no PASSADO — é dívida atrasada, e o grupo "Vencido"
 * existe exatamente para isso. `null` é estado normal ("a definir"): limpar é
 * permitido. Igual à data da venda vale (à vista). O que não existe é vencer
 * antes de a carga sair.
 */
export function checarVencimento(vencimento: string | null, dataVenda: string): FalhaDeData | null {
  if (!vencimento) return null;
  return vencimento < dataVenda ? "ANTES_DA_VENDA" : null;
}

/**
 * Recebimento é fato PASSADO: data futura não é recebimento, é previsão. E
 * dinheiro não entra antes da carga sair.
 */
export function checarDataRecebimento(data: string, dataVenda: string, hoje: string): FalhaDeData | null {
  if (data > hoje) return "NO_FUTURO";
  if (data < dataVenda) return "ANTES_DA_VENDA";
  return null;
}

/** A mensagem que o produtor lê — uma só, servidor e tela. */
export function mensagemDeFalhaDeData(falha: FalhaDeData, campo: "vencimento" | "recebimento"): string {
  if (falha === "NO_FUTURO") return "A data do recebimento não pode ser no futuro.";
  return campo === "vencimento"
    ? "O vencimento não pode ser antes da data da venda."
    : "O recebimento não pode ser antes da data da venda.";
}

/**
 * Em que caixa o usuário digitou: e-mail ou telefone.
 *
 * Puro e compartilhado porque a tela precisa saber para aplicar (ou não) a máscara
 * enquanto se digita, e o servidor precisa saber para escolher a busca. Duas
 * implementações divergiriam justo no caso chato — o número colado com "+55".
 */
export function classificarIdentificador(valor: string): "EMAIL" | "TELEFONE" | "INDEFINIDO" {
  const bruto = String(valor ?? "").trim();
  if (!bruto) return "INDEFINIDO";
  if (bruto.indexOf("@") > 0) return "EMAIL";
  const digitos = normalizarTelefoneBR(bruto);
  // Só classifica como telefone o que É um telefone: 10 dígitos (fixo antigo) a 13
  // (com o 55). Abaixo disso não dá para buscar nada, e chutar "telefone" faria a
  // tela mascarar o que o usuário digitou errado.
  if (digitos.length >= 10 && digitos.length <= 13 && !/[a-zA-Z]/.test(bruto)) return "TELEFONE";
  return "INDEFINIDO";
}

/** O que o `/v/:token` mostra antes de o corretor decidir criar a conta. */
export interface PreviaClaim {
  /** Nome do comprador como o produtor cadastrou — pré-preenche o campo. */
  nomeSugerido: string;
  /** "(74) 9****-8888" — confirma o número sem exibi-lo inteiro no link público. */
  telefoneMascarado: string;
  romaneios: number;
  produtores: number;
  /** Já existe conta para este número? Aí a tela manda entrar, não cadastrar. */
  jaTemConta: boolean;
}

/** Uma linha da lista "Romaneios recebidos" do corretor. */
export interface RomaneioRecebido {
  compartilhamentoId: string;
  produtorNome: string;
  orgProdutorId: string;
  numero: string;
  dataEmissao: string;
  vencimento: string | null;
  totalKg: number;
  totalValor: number;
  statusCarga: string;
  statusPagamento: string;
  vistoEm: string | null;
}

/**
 * KPI principal da home do corretor. ADAPTATIVO de propósito: ler dívida como
 * primeira informação da tela de boas-vindas soa como cobrança; ler o próprio
 * volume comprado soa como ferramenta. O número muda, a intenção não.
 */
export interface HomeComprador {
  compradorNome: string;
  temAberto: boolean;
  /** Quando `temAberto`. */
  valorAberto: number;
  vence7Dias: number;
  vence7DiasQtd: number;
  /** Quando NÃO há aberto — o volume do período. */
  valorComprado: number;
  kgComprado: number;
  romaneios: number;
  produtores: number;
  ultimos: RomaneioRecebido[];
}

/** Card "Fulano criou acesso e agora acompanha suas cargas". */
export interface AvisoNovoComprador {
  vinculoId: string;
  compradorNome: string;
  romaneios: number;
  criadoEm: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// UI do PRODUTOR — PR 1 (Início · Corretores · A receber)
// ═══════════════════════════════════════════════════════════════════════════
//
// Cinco itens em /mercado, cada um respondendo UMA pergunta: Início ("o que
// preciso fazer?"), Cotações ("quanto a praça paga?"), Ofertas ("quem quer minha
// carga?"), Corretores ("quem é?") e A receber ("quanto me devem?"). "Meus
// corretores" misturava "quem é" com "quanto me deve" — foi o erro da tela antiga.
//
// ⚠️ DTOs próprios, montados campo a campo. Nada aqui carrega custo, diária, chave
// de Manejo nem a linha do banco — há um teste de contrato sobre as chaves.

/**
 * Estado do corretor na visão do produtor. DERIVADO, sem coluna nova:
 * - ATIVO: o comprador está vinculado a uma org de corretor (claim feito), ou
 *   existe vínculo ATIVO;
 * - CONVIDADO: já recebeu convite (algum compartilhamento com `enviado_em`), mas
 *   ainda não criou a conta;
 * - SEM_ACESSO: nunca foi convidado.
 * ATIVO vence CONVIDADO quando os dois valem — quem já entrou não é mais convidado.
 */
export type EstadoComprador = "ATIVO" | "CONVIDADO" | "SEM_ACESSO";

export function derivarEstadoComprador(entrada: {
  temOrgVinculada: boolean;
  temVinculoAtivo: boolean;
  convidado: boolean;
}): EstadoComprador {
  if (entrada.temOrgVinculada || entrada.temVinculoAtivo) return "ATIVO";
  if (entrada.convidado) return "CONVIDADO";
  return "SEM_ACESSO";
}

/** Um card da tela Corretores. */
export interface CompradorProdutorDTO {
  compradorId: string;
  nome: string;
  /** "(74) 9****-8888" — o telefone só sai mascarado para a tela. */
  telefoneMascarado: string | null;
  /** Só dígitos, para o link wa.me. É dado do próprio produtor. */
  telefoneWhatsapp: string | null;
  cidade: string | null;
  /** CORRETOR | DISTRIBUIDOR (C3) — o chip ao lado do de vínculo. */
  perfil: string;
  /** Primeira venda para este comprador (ISO "AAAA-MM-DD") ou null. */
  desde: string | null;
  estado: EstadoComprador;
  /** Romaneios FORMALIZADOS (compartilhamentos não revogados). */
  romaneios: number;
  volumeKg: number;
  comprado: number;
  emAberto: number;
  /** Último compartilhamento (para "Reenviar convite"). */
  ultimoCompartilhamentoId: string | null;
  /** Última venda EMITIDA ainda não formalizada — é por ela que "Convidar" entra. */
  vendaParaConvidarId: string | null;
}

export interface RomaneioDoCompradorDTO {
  /** Null enquanto a venda não foi formalizada com o corretor. */
  compartilhamentoId: string | null;
  vendaId: string;
  numero: string;
  data: string;
  caixas: number;
  kg: number;
  /** Chega no C3 (conferência). Sempre null neste PR. */
  kgConferido: null;
  /** A BASE (conferido válido manda sobre o emitido) — a mesma de `ItemReceberDTO.valor`. */
  valor: number;
  /** O emitido — o documento. */
  valorEmitido: number;
  /** base − pago: o que falta receber (0 quando quitado). É o que "Recebi" sugere. */
  aReceber: number;
  vencimento: string | null;
  statusCarga: string;
  /** Ver `ItemReceberDTO.statusPagamento` — pode valer mais que PAGO/A_PAGAR (C4). */
  statusPagamento: string;
  /** O chip de dinheiro — `estadoDinheiroDaVenda`, o mesmo de Vendas. */
  dinheiro: EstadoDinheiroVenda;
  formalizacao: FormalizacaoDoRomaneio;
  enviadoEm: string | null;
  vistoEm: string | null;
}

export interface CompradorDetalheDTO {
  compradorId: string;
  nome: string;
  telefoneMascarado: string | null;
  telefoneWhatsapp: string | null;
  cidade: string | null;
  /**
   * CORRETOR | DISTRIBUIDOR (C3).
   *
   * ⚠️ Está aqui porque a tela EDITA a partir deste DTO: sem o perfil, o
   * diálogo abria no default (`CORRETOR`) e salvar rebaixava um distribuidor
   * em silêncio — bastava o produtor abrir a edição para acrescentar o
   * telefone.
   */
  perfil: string;
  estado: EstadoComprador;
  /** "conferiu N de M" — chega no C3. Null = omitir do cabeçalho. */
  conferiu: null;
  kpis: {
    emAberto: number;
    compradoAno: number;
    volumeAno: number;
    /** Média de (pago − vencimento) em dias. Null sem base — o tile NÃO é renderizado. */
    prazoMedioDias: number | null;
    /** Chega no C3. Sempre null; o tile fica oculto. */
    desvioConferencia: null;
  };
  /** Anos com romaneio, para o filtro. */
  anos: number[];
  /** Já filtrados pelo servidor (ano + pagamento). */
  romaneios: RomaneioDoCompradorDTO[];
  /** Totais da lista filtrada — o cliente só formata. */
  totais: { romaneios: number; kg: number; valor: number };
}

/** Uma linha de "Precisa de você" na home. */
export interface PendenciaDTO {
  /**
   * VENCE: vence em ≤ 3 dias (ou já venceu) e A_PAGAR.
   * NAO_ENVIADO: formalizado e nunca enviado — o corretor não sabe que a nota
   *   existe. É o estado em que o backfill do C2 deixa o histórico, e sem esta
   *   linha a home dizia "tudo em dia" sobre uma nota que ninguém recebeu.
   * NAO_ABRIU: enviado há ≥ 7 dias sem `visto_em`.
   *   ⚠️ As duas SÓ para comprador SEM conta: com conta, formalizar já pôs o
   *   romaneio na tela dele, e cobrar envio/leitura do link é pedir um ato que
   *   não falta.
   * NAO_FORMALIZADA: vendas emitidas que o corretor nem sabe que existem —
   *   AGREGADA por corretor (uma linha por comprador, não uma por venda), senão
   *   um backlog de seis notas ocuparia o teto de cinco pendências sozinho.
   */
  tipo: "NAO_ABRIU" | "NAO_ENVIADO" | "NAO_FORMALIZADA" | "VENCE";
  /** Null em NAO_FORMALIZADA (não existe compartilhamento ainda). */
  compartilhamentoId: string | null;
  /** Em NAO_FORMALIZADA é a venda mais antiga do grupo. */
  vendaId: string;
  /** Em NAO_FORMALIZADA, quantas vendas o grupo cobre. */
  numero: string;
  /**
   * ISO "AAAA-MM-DD" — a data da VENDA (em NAO_FORMALIZADA, a da mais antiga do
   * grupo). O diálogo de recebimento precisa dela: dinheiro não entra antes de a
   * carga sair, e a checagem é a mesma do servidor.
   */
  dataVenda: string;
  quantidade?: number;
  compradorId: string;
  compradorNome: string;
  valor: number;
  vencimento: string | null;
  /**
   * VENCE: dias até vencer (negativo = já venceu). NAO_ABRIU: dias desde o
   * envio. NAO_ENVIADO: dias desde a formalização.
   */
  dias: number;
}

export interface HomeProdutorDTO {
  receber: {
    total: number;
    romaneios: number;
    compradores: number;
    venceSemana: { qtd: number; valor: number } | null;
    porComprador: Array<{
      compradorId: string;
      nome: string;
      valor: number;
      status: "VENCIDO" | "VENCE_SEMANA" | "A_PAGAR";
    }>;
    recebidoMes: number;
  };
  pendencias: PendenciaDTO[];
  /** As ofertas na praça (PR 3) — o card "Ofertas em andamento". */
  ofertas: ResumoOfertaHome[];
  /**
   * "3 compradores querem sua Prata" — mora ao lado de `pendencias` e não
   * dentro dela porque `PendenciaDTO` é sobre romaneio; ver `PendenciaOfertaDTO`.
   */
  pendenciasOfertas: PendenciaOfertaDTO[];
}

export interface ItemReceberDTO {
  /** Null quando a venda ainda não foi formalizada com o corretor. */
  compartilhamentoId: string | null;
  vendaId: string;
  numero: string;
  compradorId: string;
  comprador: string;
  /**
   * ISO "AAAA-MM-DD" — a data da VENDA, não a do vencimento. É ela que ordena o
   * grupo "sem prazo" (onde não há vencimento) e que deixa a ordem legível: sem
   * a data na tela, uma lista ordenada por antiguidade parecia fora de ordem.
   */
  data: string;
  kg: number;
  kgConferido: null;
  /**
   * A BASE da cobrança: o conferido válido do comprador quando existe, senão o
   * emitido (25/09 — ver `baseDaVenda`). É o valor da linha.
   */
  valor: number;
  /** O emitido (`vendas.valor_total`) — o documento do produtor. */
  valorEmitido: number;
  /**
   * O conferido, SÓ quando difere do emitido — é o que liga a sublinha
   * "emitido R$ X · conferido R$ Y". `null` sem conferência ou quando bate.
   */
  valorConferido: number | null;
  /** O chip "Divergência" — `null` quando a conferência bate ou não há. */
  divergencia: DivergenciaDTO | null;
  /** O que já entrou (lançamentos confirmados). */
  pago: number;
  /**
   * base − pago: o que falta receber. É o que os totais somam e o que "Recebi"
   * sugere — o cliente não subtrai.
   */
  aReceber: number;
  vencimento: string | null;
  statusCarga: string;
  /**
   * ⚠️ Pode valer mais que PAGO/A_PAGAR desde o C4: com lançamentos, é o
   * `StatusDinheiro` inteiro (INFORMADO, PARCIAL, PARCIAL_INFORMADO,
   * CONTESTADO). Sem lançamento nenhum continua caindo em PAGO/A_PAGAR — o
   * histórico não foi migrado, e não podia ser: um lançamento inventado a partir
   * de `paga_em` afirmaria que alguém informou e alguém confirmou um valor que
   * ninguém digitou.
   */
  statusPagamento: string;
  /** O chip de dinheiro — `estadoDinheiroDaVenda`, o mesmo de Vendas. */
  dinheiro: EstadoDinheiroVenda;
  /**
   * Onde a nota está no caminho até o corretor — EIXO PRÓPRIO, ao lado do
   * dinheiro. Ver `derivarFormalizacao`: termina em FORMALIZADA, e o link só
   * aparece (como sublinha) quando o comprador não tem conta.
   */
  formalizacao: FormalizacaoDoRomaneio;
  enviadoEm: string | null;
  vistoEm: string | null;
  /** Dias até vencer (negativo = vencido); null sem vencimento. */
  dias: number | null;
}

export interface GrupoReceberDTO {
  total: number;
  itens: ItemReceberDTO[];
}

export interface ReceberDTO {
  kpis: {
    emAberto: number;
    venceSemana: number;
    vencido: number;
    recebidoMes: number;
    /** Contagens da linha de meta dos tiles — o cliente não recontar nada. */
    emAbertoRomaneios: number;
    emAbertoCompradores: number;
    recebidoMesQtd: number;
  };
  /**
   * Os quatro baldes são só de DATA — a pergunta que a tela responde é quando o
   * dinheiro entra.
   *
   * ⚠️ `semVencimento` chamava-se `semPrazo` e o título dizia "Sem prazo · falta
   * formalizar", juntando um problema de DADO (não tem vencimento) com um de
   * PROCESSO (não foi formalizado) — dois estados independentes: dá para ter
   * prazo sem formalizar, e formalizar sem prazo. Com o vencimento editável
   * (061) o balde é só "ainda não tem data"; a formalização virou pendência POR
   * LINHA, onde já estava (o marcador sob o número e o botão Formalizar).
   */
  grupos: { vencidas: GrupoReceberDTO; semana: GrupoReceberDTO; proximas: GrupoReceberDTO; semVencimento: GrupoReceberDTO };
  /**
   * A semana-CALENDÁRIO corrente (segunda a domingo), para o título do grupo
   * dizer que intervalo ele cobre: "Esta semana · 07 a 13/09". Sem isso a tela
   * tinha duas definições de semana ao mesmo tempo — o grupo ia até domingo e a
   * linha contava "em 7 d" em dias corridos.
   */
  janelaSemana: { inicio: string; fim: string };
  porComprador: Array<{ compradorId: string; nome: string; total: number; itens: ItemReceberDTO[] }>;
}

/**
 * Chaves que NUNCA podem aparecer num DTO do produtor no /mercado — o teste de
 * contrato varre os DTOs recursivamente e falha se encontrar qualquer uma.
 */
export const CHAVES_PROIBIDAS_DTO_PRODUTOR = ["custo", "diaria", "mnj", "manejo", "orgId", "org_id", "usuarioId"] as const;
